import { removeNull } from '@/utils/handleData'
import {
  buildMixingScanQcodeInfo,
  buildShipmentSignQcodeInfo
} from '@/utils/scanQrcode'

// 任务状态映射
const taskStatusMap = {
  0: '待生产',
  1: '生产中',
  2: '生产完成',
  3: '已暂停',
  4: '已中止',
  5: '作废拉运中(历史)',
  6: '已中止待处置',
  7: '不合格待处置'
}

function formatSigningTime(val) {
  if (!val) return ''
  const text = String(val).trim().replace('T', ' ')
  return text.length >= 16 ? text.slice(0, 16) : text
}

function decorateProductionItem(item) {
  item.disabledButtons = []
  item.taskStatusCn = taskStatusMap[item.taskStatus] || item.taskStatus
  // 与浇筑令列表一致：分项名称(部位)
  const order = item.orderInfo || {}
  const subitemName = order.subitemName || item.subitemName || ''
  const remarks = order.remarks || ''
  if (subitemName || remarks) {
    item.pouringLocation = remarks
      ? subitemName + '(' + remarks + ')'
      : subitemName || item.pouringLocation || ''
  } else {
    item.pouringLocation = item.pouringLocation || item.subitemName || ''
  }
  item.queueNoText =
    item.taskStatus === '0' ||
    item.taskStatus === '3' ||
    item.taskStatus === '6' ||
    item.taskStatus === '7'
      ? item.queueNo != null && item.queueNo !== ''
        ? String(item.queueNo)
        : '-'
      : item.taskStatus === '1'
      ? '产'
      : item.taskStatus === '5'
      ? '废运'
      : '-'
  item.timeText =
    '计 ' +
    (item.displayPlanTime || item.plannedPouringTime || '-') +
    '\n始 ' +
    (item.displayStartTime || item.firstStartTime || item.startTime || '-') +
    '\n完 ' +
    (item.displayFinishTime || item.finishTime || '-')
  item.productionProgress =
    '生\xa0' +
    (item.producedQuantity || 0) +
    '\xa0|\xa0装\xa0' +
    (item.waitingQuantity || 0) +
    '\xa0|\xa0运\xa0' +
    (item.shippingQuantity || 0) +
    '\xa0|\xa0签\xa0' +
    (item.signedQuantity || 0) +
    '\xa0|\xa0废\xa0' +
    (item.abandonedQuantity || 0)
  item.appliedTypeTag = item.appliedType === '补方'
  item.shipmentList = item.shipmentList ? item.shipmentList : []
  item.currentBatchQuantity = item.currentBatchQuantity || 0
  if (item.appliedQuantity == null || item.appliedQuantity === '') {
    item.appliedQuantity = item.plannedQuantity || 0
  }
  if (!item.signingOpinionText) {
    const opinions = item.shipmentList
      .filter(
        (ship) =>
          ship && ship.signingOpinion && String(ship.signingOpinion).trim()
      )
      .sort((a, b) => {
        const ta = a.signingTime || a.updateTime || ''
        const tb = b.signingTime || b.updateTime || ''
        return String(ta).localeCompare(String(tb))
      })
      .map((ship) => {
        const time = formatSigningTime(ship.signingTime || ship.updateTime)
        const opinion = String(ship.signingOpinion).trim()
        return time ? time + ' ' + opinion : opinion
      })
    item.signingOpinionText = opinions.join('\n')
  }
  return item
}

export default {
  namespaced: true,
  state: {
    productionList: {}, // 生产任务列表
    productionInfo: {}, // 生产任务详情
    orderAppliedDetail: {}, // 浇筑令+本任务申请详情
    productionQrcode: '', // 拌合站固定二维码内容
    signTransferTargets: [], // 签收转移候选任务
    transferableList: [], // 中止转移候选任务
    orderTaskOptions: [], // 浇筑令下可挂接的生产任务（物料运输用）
    myMixingStations: [], // 当前用户关联的混拌站及设备
    busyCarUids: [], // 生产中占用的拌和设备
    shipmentLoadTargets: [] // 运输装车可改挂的同标号任务
  },
  getters: {},
  actions: {
    // 独立加载「生产中」任务栏数据，不受列表筛选/关键词影响
    async loadCurrentProducingTasks({ rootState, commit, dispatch }, data) {
      const orgUid =
        (data && data.orgUid) || rootState.user.orgValue || ''
      if (!orgUid) {
        return []
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductionlist',
          data: {
            orgUid: orgUid,
            taskStatus: '1',
            group: 'active',
            query: '',
            page: 1,
            limit: 100
          },
          notShowError: true
        },
        { root: true }
      )
      const payload = res && res.data ? res.data : {}
      const rows = Array.isArray(payload.result) ? payload.result : []
      const currentTasks = rows.map((item) => decorateProductionItem(item))
      const currentTask = currentTasks.length ? currentTasks[0] : null
      const prev = rootState.mixingProduction.productionList || {}
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'productionList',
          value: Object.assign({}, prev, {
            currentTask: currentTask,
            currentTasks: currentTasks,
            hasProducing: currentTasks.length > 0
          })
        },
        { root: true }
      )
      return currentTasks
    },
    // 获取拌合站生产任务队列列表（后端自动同步审核通过的申请并维护队列）
    // group: active=待生产/生产中/已暂停/作废拉运（默认），finished=已结束（仅查询）
    async getMixingProductionList({ rootState, commit, dispatch }, data) {
      var queryValues = {
        orgUid: data && data.orgUid ? data.orgUid : rootState.user.orgValue,
        taskStatus:
          data && data.taskStatus !== undefined
            ? data.taskStatus
            : rootState.queryValues.mixingProduction &&
              rootState.queryValues.mixingProduction.taskStatus
            ? rootState.queryValues.mixingProduction.taskStatus
            : '',
        group:
          data && data.group !== undefined
            ? data.group
            : rootState.queryValues.mixingProduction &&
              rootState.queryValues.mixingProduction.group
            ? rootState.queryValues.mixingProduction.group
            : 'active',
        query:
          data && data.query
            ? data.query
            : rootState.queryValues.mixingProduction &&
              rootState.queryValues.mixingProduction.query
            ? rootState.queryValues.mixingProduction.query
            : '',
        page: data && data.page ? data.page : rootState.page.mixingProduction,
        limit: data && data.limit ? data.limit : rootState.limit.mixingProduction
      }
      queryValues = removeNull(queryValues)
      const group = queryValues.group || 'active'
      const prevList = rootState.mixingProduction.productionList || {}
      const [res] = await Promise.all([
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/fixed/mixingproductionlist',
            data: queryValues
          },
          { root: true }
        ),
        // 任务栏与列表筛选解耦：并行刷新生产中任务
        dispatch('loadCurrentProducingTasks', { orgUid: queryValues.orgUid })
      ])
      var productionList = res.data ? res.data : {}
      if (!Array.isArray(productionList.result)) {
        productionList.result = []
      }
      productionList.result.forEach((item) => {
        decorateProductionItem(item)
      })
      // 行按钮索引：0开始 1置顶 2上移 3下移
      productionList.result.forEach((item) => {
        if (group === 'finished') {
          item.disabledButtons.push(0, 1, 2, 3)
        } else if (item.taskStatus === '0' || item.taskStatus === '3') {
          // 可开始、可调序
        } else if (item.taskStatus === '1') {
          // 生产中：禁用开始（当前任务栏暂停）
          item.disabledButtons.push(0, 1, 2, 3)
        } else if (item.taskStatus === '6' || item.taskStatus === '7') {
          // 待处置：不可开始，可调序
          item.disabledButtons.push(0)
        } else {
          item.disabledButtons.push(0, 1, 2, 3)
        }
      })
      // 保留并行请求写入的 currentTasks，避免被列表结果覆盖
      const latest = rootState.mixingProduction.productionList || {}
      productionList.currentTasks =
        latest.currentTasks || prevList.currentTasks || []
      productionList.currentTask =
        latest.currentTask || prevList.currentTask || null
      productionList.hasProducing =
        latest.hasProducing != null
          ? latest.hasProducing
          : productionList.currentTasks.length > 0
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'productionList',
          value: productionList
        },
        { root: true }
      )
      // 同步当前用户拌和站与占用设备，供开始生产与顶部任务栏过滤
      await dispatch('loadMyMixingStations')
      return productionList
    },
    async loadMyMixingStations({ rootState, commit, dispatch }, orgUid) {
      const uid = orgUid || rootState.user.orgValue
      if (!uid) {
        commit(
          'baseSet',
          { module: 'mixingProduction', key: 'myMixingStations', value: [] },
          { root: true }
        )
        commit(
          'baseSet',
          { module: 'mixingProduction', key: 'busyCarUids', value: [] },
          { root: true }
        )
        return []
      }
      const [stationRes, busyRes] = await Promise.all([
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/template/orgfactory/mymixing',
            data: { orgUid: uid },
            notShowError: true
          },
          { root: true }
        ),
        dispatch(
          'baseRequest',
          {
            method: 'GET',
            url: '/organize/fixed/mixingproductionbusycars',
            data: { orgUid: uid },
            notShowError: true
          },
          { root: true }
        )
      ])
      const stations = stationRes && stationRes.data ? stationRes.data : []
      const busy =
        busyRes && busyRes.data
          ? Array.isArray(busyRes.data)
            ? busyRes.data
            : []
          : []
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'myMixingStations',
          value: stations
        },
        { root: true }
      )
      commit(
        'baseSet',
        { module: 'mixingProduction', key: 'busyCarUids', value: busy },
        { root: true }
      )
      return stations
    },
    // 获取生产任务详情（含浇筑令、本任务对应运输单）
    async getMixingProductionInfo({ rootState, commit, dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductioninfo',
          data: { uid: uid }
        },
        { root: true }
      )
      var productionInfo = res.data ? res.data : {}
      productionInfo.taskStatusCn = taskStatusMap[productionInfo.taskStatus]
      productionInfo.shipmentList = productionInfo.shipmentList
        ? productionInfo.shipmentList
        : []
      productionInfo.shipmentList.forEach((list) => {
        list.appliedRemarks = list.remarks
        list.monitoring = rootState.officialCar.carList.find((car) => {
          return car.carPlate === list.carCode
        })
        list.signQcodeInfo = buildShipmentSignQcodeInfo(list)
        list.qcodeInfo = list.signQcodeInfo
        // 按钮：0处置 1编辑 2发车 3删除 4监控 5二维码
        list.disabledButtons = []
        const status = list.shipmentStatus
        if (status === '待装车') {
          // 可处置、编辑、删除；不可发车（须先绑车变为待发车）
          list.disabledButtons.push(2)
        } else if (status === '待发车') {
          // 处置/编辑/发车/删除均可
        } else if (
          status === '运输中' ||
          status === '不合格待处置' ||
          status === '已中止待处置' ||
          status === '已终止待处置'
        ) {
          // 可处置；不可编辑/发车/删除
          list.disabledButtons.push(1, 2, 3)
        } else {
          list.disabledButtons.push(0, 1, 2, 3)
        }
        if (!list.monitoring) {
          list.disabledButtons.push(4)
        }
      })
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'productionInfo',
          value: productionInfo
        },
        { root: true }
      )
      return productionInfo
    },
    // 浇筑令编号下钻：浇筑令摘要 + 本任务对应申请/补方
    async getOrderAppliedDetail({ commit, dispatch }, row) {
      const orderUid = row && row.orderUid
      if (!orderUid) {
        commit(
          'baseSet',
          {
            module: 'mixingProduction',
            key: 'orderAppliedDetail',
            value: {}
          },
          { root: true }
        )
        return {}
      }
      const orderInfo = await dispatch(
        'pouringOrder/getPouringOrderInfo',
        orderUid,
        { root: true }
      )
      const appliedList = (orderInfo && orderInfo.appliedList) || []
      const matched = row.applicationUid
        ? appliedList.filter((item) => item.uid === row.applicationUid)
        : []
      const detail = {
        pouringOrderCode: (orderInfo && orderInfo.pouringOrderCode) || row.pouringOrderCode,
        pouringLocation:
          (orderInfo && orderInfo.pouringLocation) ||
          row.pouringLocation ||
          row.subitemName,
        strengthGrade: (orderInfo && orderInfo.strengthGrade) || row.strengthGrade,
        designQuantity: orderInfo && orderInfo.designQuantity,
        plannedPouringTime:
          (orderInfo && orderInfo.plannedPouringTime) || row.plannedPouringTime,
        taskCode: row.taskCode,
        taskStatusCn: taskStatusMap[row.taskStatus] || row.taskStatusCn,
        applicationUid: row.applicationUid,
        appliedList: matched.length ? matched : appliedList
      }
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'orderAppliedDetail',
          value: detail
        },
        { root: true }
      )
      return detail
    },
    // 手动调整队列（up上移/down下移/top置顶）
    async moveMixingProductionTask({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionsort',
          data: data
        },
        { root: true }
      )
      return res
    },
    // 按规则自动重排队列（补方优先、计划浇筑时间升序）
    async autoSortMixingProductionQueue({ rootState, dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionsort',
          data: {
            orgUid: data && data.orgUid ? data.orgUid : rootState.user.orgValue,
            auto: 'true'
          }
        },
        { root: true }
      )
      return res
    },
    // 生产任务状态流转（开始生产需传batchQuantity本次生产方量）
    async changeMixingProductionStatus({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionstatus',
          data: data
        },
        { root: true }
      )
      return res
    },
    // 按浇筑令查可接收在途运输单转移的待生产任务
    async getTransferableForOrder({ dispatch }, orderUid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductiontransferableorder',
          data: { orderUid: orderUid },
          notShowError: true
        },
        { root: true }
      )
      return res && res.data ? res.data : []
    },
    async loadSignTransferTargets({ commit, dispatch }, orderUid) {
      const list = await dispatch('getTransferableForOrder', orderUid)
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'signTransferTargets',
          value: list || []
        },
        { root: true }
      )
      return list
    },
    // 按浇筑令查可挂接生产任务（物料运输「生产单信息」）
    async getTasksByOrder({ commit, dispatch }, orderUid) {
      if (!orderUid) {
        commit(
          'baseSet',
          {
            module: 'mixingProduction',
            key: 'orderTaskOptions',
            value: []
          },
          { root: true }
        )
        return []
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductionbyorder',
          data: { orderUid: orderUid },
          notShowError: true
        },
        { root: true }
      )
      const list = (res && res.data ? res.data : []).map((item) => {
        const loadable =
          item.taskStatus === '1' || item.taskStatus === 1
            ? item.currentBatchQuantity
            : item.remainQuantity
        const qty =
          loadable != null && loadable !== '' ? Number(loadable) : null
        const pendingTag = item.hasPendingLoad ? '【待装车】' : ''
        const statusText = item.taskStatusCn
          ? '（' + item.taskStatusCn + '）'
          : item.taskStatus === '1'
          ? '（生产中）'
          : item.taskStatus === '0'
          ? '（待生产）'
          : item.taskStatus === '3'
          ? '（已暂停）'
          : ''
        const qtyText =
          qty != null && !isNaN(qty) ? ' 可装' + qty + 'm³' : ''
        return Object.assign({}, item, {
          value: item.uid,
          label:
            (item.taskCode || '-') + statusText + qtyText + pendingTag
        })
      })
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'orderTaskOptions',
          value: list
        },
        { root: true }
      )
      return list
    },
    // 中止浇筑令在途运输单转移挂接
    async transferShipment({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/transportationordertransfer',
          data: data
        },
        { root: true }
      )
      return res
    },
    async resetProducedByShipments({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionresetproduced',
          data: { uid: uid }
        },
        { root: true }
      )
      return res
    },
    // 编辑生产任务（已生产方量/备注）
    async editMixingProduction({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproduction',
          data: data,
          msg: '编辑生产任务成功'
        },
        { root: true }
      )
      return res
    },
    // 获取拌合站固定二维码（走统一 page-table 二维码弹窗）
    async getMixingProductionQrcode(
      { rootState, rootGetters, commit, dispatch },
      orgUid
    ) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductionqrcode',
          data: { orgUid: orgUid ? orgUid : rootState.user.orgValue }
        },
        { root: true }
      )
      var productionQrcode = res.data ? res.data.qcode : ''
      const mixStation =
        (res.data &&
          (res.data.mixStationName ||
            res.data.stationName ||
            res.data.factoryName)) ||
        ''
      const mixerNo =
        (res.data &&
          (res.data.mixerName ||
            res.data.mixerCode ||
            res.data.mixerNo ||
            res.data.machineName)) ||
        ''
      const orgKey = orgUid ? orgUid : rootState.user.orgValue
      const qcodeInfo = buildMixingScanQcodeInfo(orgKey, {
        qcodeName: rootGetters['user/orgName'] || '拌合站生产码',
        leftText: '拌合生产',
        rightText: '扫码装料',
        project: [mixStation, mixerNo].filter(Boolean).join(' ')
      })
      // 保留后端明文作兜底展示对比
      if (!qcodeInfo.qcodeUrl && productionQrcode) {
        qcodeInfo.qcodeUrl = productionQrcode
      }
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'productionQrcode',
          value: productionQrcode
        },
        { root: true }
      )
      return qcodeInfo
    },
    // 可接收方量转移的候选任务（同标号待生产）
    async getTransferableTasks({ commit, dispatch }, taskUid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductiontransferable',
          data: { uid: taskUid },
          notShowError: true
        },
        { root: true }
      )
      const list = res && res.data ? res.data : []
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'transferableList',
          value: list
        },
        { root: true }
      )
      return list
    },
    // 运输装车改挂候选：当前任务 + 同标号待生产队列
    async loadShipmentLoadTargets({ commit, dispatch }, taskUid) {
      if (!taskUid) {
        commit(
          'baseSet',
          {
            module: 'mixingProduction',
            key: 'shipmentLoadTargets',
            value: []
          },
          { root: true }
        )
        return { tasks: [] }
      }
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductionshiploadtargets',
          data: { uid: taskUid },
          notShowError: true
        },
        { root: true }
      )
      const data = (res && res.data) || {}
      const tasks = (data.tasks || []).map((item) => {
        const statusText =
          item.taskStatusCn ||
          (item.taskStatus === '1'
            ? '生产中'
            : item.taskStatus === '0'
            ? '待生产'
            : item.taskStatus === '3'
            ? '已暂停'
            : item.taskStatus || '')
        return Object.assign({}, item, {
          value: item.uid,
          label:
            (item.taskCode || '-') +
            (statusText ? '（' + statusText + '）' : '') +
            (item.pouringOrderCode ? ' / ' + item.pouringOrderCode : '')
        })
      })
      commit(
        'baseSet',
        {
          module: 'mixingProduction',
          key: 'shipmentLoadTargets',
          value: tasks
        },
        { root: true }
      )
      return Object.assign({}, data, { tasks: tasks })
    },
    // 生产中任务中止处理：transfer转移方量 / abandon作废拉运
    async terminateMixingProductionTask({ dispatch }, data) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionterminate',
          data: data
        },
        { root: true }
      )
      return res
    },
    // 浇筑令中止未读通知（拌合站轮询弹窗）
    async getMixingProductionAlerts({ rootState, dispatch }, orgUid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'GET',
          url: '/organize/fixed/mixingproductionalerts',
          data: { orgUid: orgUid ? orgUid : rootState.user.orgValue },
          notShowError: true
        },
        { root: true }
      )
      return res && res.data ? res.data : []
    },
    // 中止通知确认已读
    async readMixingProductionAlert({ dispatch }, uid) {
      const res = await dispatch(
        'baseRequest',
        {
          method: 'PUT',
          url: '/organize/fixed/mixingproductionalertread',
          data: { uid: uid }
        },
        { root: true }
      )
      return res
    }
  }
}
